import { useState } from 'react';
import clsx from 'clsx';

import { TeamCrest } from '@/entities/match';
import type { Match } from '@/entities/match';
import {
  type CreatePredictionPayload,
  type FirstGoalOutcome,
  type MatchOutcome,
  PredictionType,
} from '@/entities/prediction';
import type { OutcomeDistribution, TotalDistribution } from '@/shared/api';
import { formatMoscowDateTime } from '@/shared/lib/format';
import { RADIUS, Squircle, StaticSquircle } from '@/shared/ui';

import {
  leaderOf,
  toPercents,
  toTotalPercents,
  totalLeaderOf,
} from '../model/distribution';
import { useCreatePrediction } from '../model/use-create-prediction';

import styles from './PredictionForm.module.scss';

interface PredictionFormProps {
  match: Match;
  /** Крестик в шапке шторки — узел 438:50951. */
  onClose?: () => void;
}

const OUTCOME_LABELS: Record<MatchOutcome, string> = {
  HOME: 'Победа 1',
  DRAW: 'Ничья',
  AWAY: 'Победа 2',
};

/**
 * Заголовок вкладки-пилюли. В ките названы «Исход матча» и «Голы»
 * (438:50966) — Первого гола там нет вовсе.
 *
 * BOTH_TEAMS_TO_SCORE/TOTAL_OVER_UNDER — из более свежего узла
 * (824:9156, «Обе забьют»/«Тотал»). Оба типа сервер отдаёт со второго
 * Модуля: до начала цикла 2 они просто не приходят в `availableTypes`.
 */
const TAB_LABELS: Record<PredictionType, string> = {
  MATCH_OUTCOME: 'Исход матча',
  EXACT_SCORE: 'Голы',
  FIRST_GOAL: 'Первый гол',
  BOTH_TEAMS_TO_SCORE: 'Обе забьют',
  TOTAL_OVER_UNDER: 'Тотал',
};

/**
 * Вопрос над Да/Нет у бинарных типов (824:9156) — в ките такой подписи нет
 * ни у одного из уже реализованных типов, только у этих двух новых.
 *
 * TODO(design): текст под TOTAL_OVER_UNDER в ките испорчен («Обе команды
 * забьют больше 2.5 да/нет.» — очевидно склейка с описанием BTTS),
 * поэтому формулировка наша.
 *
 * ПОРОГ НЕ ЗАШИТ. Его присылает сервер (`AvailableType.totalLine`), и
 * правит его админка: это балансная настройка, а не текст. Зашитое
 * число лгало бы игроку при первой же правке — он поставил бы Прогноз
 * не на то, что прочитал.
 *
 * Порога нет — вопроса нет: выдумать его хуже, чем промолчать.
 */
function questionFor(
  type: PredictionType,
  totalLine: number | undefined,
): string | undefined {
  if (type === PredictionType.BOTH_TEAMS_TO_SCORE) {
    return 'Обе команды забьют голы в этом матче';
  }
  if (type === PredictionType.TOTAL_OVER_UNDER && totalLine !== undefined) {
    return `Тотал голов больше ${String(totalLine)}`;
  }
  return undefined;
}

/**
 * Границы счёта на случай, если сервер их не прислал.
 *
 * Не выдуманные числа: ровно те же значения подставляет
 * `predictions.service.ts` при проверке (`?? 0`, `?? 99`). Здесь они нужны,
 * чтобы степпер не уехал в минус; решает всё равно сервер.
 */
const SCORE_MIN = 0;
const SCORE_MAX = 99;

/**
 * Шторка Прогноза на матч (узел 438:50949 «Исход матча», 438:51007 «Голы»).
 *
 * Набор типов задаёт Модуль Месячного цикла, и приходит он в карточке
 * (`availableTypes`) — константы на клиенте тут быть не может. Каждый тип
 * подтверждается отдельно и стоит своих Фанкоинов: ГДД §5.3 разрешает
 * не более одного Прогноза каждого типа на матч.
 *
 * «Распределение прогнозов» (узел 438:50968) рисуется **только у Исхода**
 * и стоит первым в панели, выше стоимости и кнопок. Агрегат приходит
 * в карточке (`outcomeDistribution`) и считается по настоящим Прогнозам
 * всех игроков. У «Точного счёта» вариантов сто на сто — полоска из них
 * не собирается; у «Первого гола» такого блока нет в ките.
 *
 * Пока никто не выбрал, блок не показывается вовсе: «0% / 0% / 0%» и
 * поровну поделённая полоска одинаково врут о том, чего ещё нет.
 *
 * «Первый гол» своего узла в ките не получил (только «Исход матча»/«Голы» —
 * последняя это EXACT_SCORE): рисуется тем же пилюльным стилем, что и Исход,
 * но раскладка (три варианта: команда/команда/без гола) не выверена пиксельно.
 */
export function PredictionForm({ match, onClose }: PredictionFormProps) {
  const types = match.availableTypes;
  // Открытый тип выбирается один раз при монтировании (ленивый инициализатор
  // не перезапускается на ре-рендерах) — сохранение переводит active.type
  // в alreadyPredicted, но вкладку игрок должен переключить сам, а не
  // получить автоматический прыжок на следующий тип.
  const [chosen, setChosen] = useState<PredictionType | null>(() => {
    const firstOpen = types.find((type) => !type.alreadyPredicted);
    return (firstOpen ?? types[0])?.type ?? null;
  });
  // `null` — «игрок в этой сессии ещё ничего не трогал», а не «выбрано
  // пусто». Отличать нужно: при пустом значении показывается уже
  // сохранённый Прогноз, см. `shown*` ниже.
  const [outcome, setOutcome] = useState<MatchOutcome | null>(null);
  const [firstGoal, setFirstGoal] = useState<FirstGoalOutcome | null>(null);
  const [homeScore, setHomeScore] = useState<number | null>(null);
  const [awayScore, setAwayScore] = useState<number | null>(null);
  const [bothTeamsScore, setBothTeamsScore] = useState<boolean | null>(null);
  const [totalOver, setTotalOver] = useState<boolean | null>(null);
  const { submit, isSubmitting, error } = useCreatePrediction();

  // Ни одного типа в Модуле — формы быть не должно вовсе
  const first = types[0];
  if (first === undefined) return null;

  const active = types.find((type) => type.type === chosen) ?? first;

  const isLocked = active.alreadyPredicted || !match.isOpenForPredictions;
  const minScore = active.minScoreValue ?? SCORE_MIN;
  const maxScore = active.maxScoreValue ?? SCORE_MAX;

  /**
   * Уже поставленный Прогноз этого типа, если он есть.
   *
   * Форма его РАНЬШЕ НЕ ЧИТАЛА: состояние начиналось пустым, и стоило
   * закрыть шторку и открыть заново, как выбор игрока пропадал —
   * подсветки не было, счёт показывался 0:0. Внутри одной сессии всё
   * выглядело верно, потому что локальное состояние переживало
   * сохранение, и дефект был не виден.
   */
  const existing =
    match.myPredictions.find((prediction) => prediction.type === active.type) ??
    null;

  // Что показываем: выбор игрока в этой сессии, а если его не было —
  // сохранённый Прогноз. Порядок именно такой, иначе незакрытая шторка
  // затирала бы только что выбранное значение сохранённым.
  const shownOutcome = outcome ?? existing?.outcome ?? null;
  const shownFirstGoal = firstGoal ?? existing?.firstGoal ?? null;
  const shownHome = homeScore ?? existing?.homeScoreGuess ?? 0;
  const shownAway = awayScore ?? existing?.awayScoreGuess ?? 0;
  const shownBothTeamsScore =
    bothTeamsScore ?? existing?.bothTeamsScoreGuess ?? null;
  const shownTotalOver = totalOver ?? existing?.totalOverGuess ?? null;

  const payload = buildPayload(match.id, active.type, {
    outcome: shownOutcome,
    firstGoal: shownFirstGoal,
    homeScore: shownHome,
    awayScore: shownAway,
    bothTeamsScore: shownBothTeamsScore,
    totalOver: shownTotalOver,
  });

  // Выбор не сбрасывается после сохранения: тип становится alreadyPredicted
  // и блокируется, а выбранная кнопка должна остаться зелёной — это и есть
  // подтверждение, что тут отметил игрок.
  const handleSubmit = async (): Promise<void> => {
    if (!payload) return;
    await submit(payload);
  };

  const clamp = (value: number): number =>
    Math.min(maxScore, Math.max(minScore, value));

  return (
    <form
      className={styles.sheet}
      data-testid="prediction-form"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <div className={styles.closeRow}>
        <StaticSquircle asChild width={24} height={24} cornerRadius={RADIUS.sm}>
          <button
            type="button"
            className={styles.close}
            aria-label="Закрыть"
            data-testid="prediction-close"
            onClick={onClose}
          >
            <img
              className={styles.closeIcon}
              src="/images/matches/close.svg"
              alt=""
              aria-hidden
            />
          </button>
        </StaticSquircle>
      </div>

      <p className={styles.time}>{formatMoscowDateTime(match.startsAt)}</p>

      <div className={styles.teams}>
        <TeamColumn team={match.homeTeam} />
        <span className={styles.versus}>X</span>
        <TeamColumn team={match.awayTeam} />
      </div>

      <Squircle cornerRadius={RADIUS.xl} className={styles.panel}>
        {types.length > 1 ? (
          // Без галочки за сделанный тип: в ките и в макете (471:60924)
          // её нет — состояние «сделан» показывает только кнопка снизу.
          <div className={styles.tabs} role="tablist" aria-label="Тип Прогноза">
            {types.map((type) => (
              <Squircle key={type.type} asChild cornerRadius={RADIUS.xl}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={type.type === active.type}
                  className={clsx(
                    styles.tab,
                    type.type === active.type && styles.tabActive,
                  )}
                  data-selected={type.type === active.type}
                  data-testid={`type-${type.type}`}
                  onClick={() => {
                    setChosen(type.type);
                  }}
                >
                  {TAB_LABELS[type.type]}
                </button>
              </Squircle>
            ))}
          </div>
        ) : null}

        <div className={styles.content}>
          {/* Узел 438:50968 стоит первым в панели — выше стоимости и кнопок */}
          {active.type === PredictionType.MATCH_OUTCOME &&
          match.outcomeDistribution.total > 0 ? (
            <OutcomeDistributionBar distribution={match.outcomeDistribution} />
          ) : null}

          {/* «Прогноз игроков» — тот же приём для TOTAL_OVER_UNDER.
              Поле приходит всегда, поэтому проверяем только наполнение:
              ноль означает «никто ещё не выбрал», а не «сервер не прислал» */}
          {active.type === PredictionType.TOTAL_OVER_UNDER &&
          match.totalDistribution.total > 0 ? (
            <TotalDistributionBar
              distribution={match.totalDistribution}
              totalLine={active.totalLine}
            />
          ) : null}

          <div className={styles.costRow}>
            <p className={styles.costLabel}>Стоимость прогноза:</p>
            <span className={styles.costValue} data-testid="prediction-reward">
              <img
                className={styles.costIcon}
                src="/images/header/predict-coin.svg"
                alt=""
                aria-hidden
              />
              {active.funcoinCost}
            </span>
          </div>

          {active.type === PredictionType.EXACT_SCORE ? (
            <div className={styles.scoreBlock} data-testid="score-input">
              <div className={styles.scoreLabels}>
                <span>{match.homeTeam.name}</span>
                <span>{match.awayTeam.name}</span>
              </div>
              <div className={styles.steppers}>
                <ScoreStepper
                  side="home"
                  value={shownHome}
                  disabled={isLocked}
                  onChange={(next) => {
                    setHomeScore(clamp(next));
                  }}
                />
                <ScoreStepper
                  side="away"
                  value={shownAway}
                  disabled={isLocked}
                  onChange={(next) => {
                    setAwayScore(clamp(next));
                  }}
                />
              </div>
            </div>
          ) : active.type === PredictionType.FIRST_GOAL ? (
            <div
              className={styles.outcomes}
              role="radiogroup"
              aria-label="Кто забьёт первым"
            >
              {/* Названия команд, а не П1/П2: «первый гол за П1» не читается */}
              {(
                [
                  ['HOME', match.homeTeam.shortName ?? match.homeTeam.name],
                  ['AWAY', match.awayTeam.shortName ?? match.awayTeam.name],
                  ['NONE', 'Без голов'],
                ] as [FirstGoalOutcome, string][]
              ).map(([value, label]) => (
                <Squircle key={value} asChild cornerRadius={RADIUS.md}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={shownFirstGoal === value}
                    className={styles.outcome}
                    data-selected={shownFirstGoal === value}
                    data-testid={`first-goal-${value}`}
                    disabled={isLocked}
                    onClick={() => {
                      setFirstGoal(value);
                    }}
                  >
                    {label}
                  </button>
                </Squircle>
              ))}
            </div>
          ) : active.type === PredictionType.BOTH_TEAMS_TO_SCORE ? (
            <BinaryQuestion
              question={questionFor(active.type, active.totalLine)}
              value={shownBothTeamsScore}
              disabled={isLocked}
              onChange={setBothTeamsScore}
            />
          ) : active.type === PredictionType.TOTAL_OVER_UNDER ? (
            <BinaryQuestion
              question={questionFor(active.type, active.totalLine)}
              value={shownTotalOver}
              disabled={isLocked}
              onChange={setTotalOver}
            />
          ) : (
            <div
              className={styles.outcomes}
              role="radiogroup"
              aria-label="Исход"
            >
              {(Object.keys(OUTCOME_LABELS) as MatchOutcome[]).map((value) => (
                <Squircle key={value} asChild cornerRadius={RADIUS.md}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={shownOutcome === value}
                    className={styles.outcome}
                    data-selected={shownOutcome === value}
                    data-testid={`outcome-${value}`}
                    disabled={isLocked}
                    onClick={() => {
                      setOutcome(value);
                    }}
                  >
                    {OUTCOME_LABELS[value]}
                  </button>
                </Squircle>
              ))}
            </div>
          )}

          <p className={styles.rewardHint}>
            Если прогноз окажется верным, ты получишь{' '}
            <span className={styles.rewardValue}>
              {active.basePoints} очков
            </span>
          </p>
        </div>

        {error ? (
          <p
            className={styles.error}
            role="alert"
            data-testid="prediction-error"
          >
            {error}
          </p>
        ) : null}

        <div className={styles.submitBlock}>
          <Squircle asChild cornerRadius={RADIUS.xl}>
            <button
              type="submit"
              className={styles.save}
              disabled={isLocked || payload === null || isSubmitting}
            >
              {active.alreadyPredicted
                ? 'Прогноз сделан'
                : 'Сохранить прогноз!'}
            </button>
          </Squircle>
          <p className={styles.warning}>
            После сохранения прогноз нельзя будет изменить
          </p>
        </div>
      </Squircle>
    </form>
  );
}

function TeamColumn({ team }: { team: Match['homeTeam'] }) {
  return (
    <div className={styles.team}>
      <StaticSquircle
        width={100}
        height={100}
        cornerRadius={RADIUS.lg}
        className={styles.crest}
      >
        <TeamCrest
          name={team.name}
          logoUrl={team.logoUrl}
          imageClassName={styles.crestImage}
          fallbackClassName={styles.crestFallback}
        />
      </StaticSquircle>
      <p className={styles.teamName}>{team.name}</p>
    </div>
  );
}

interface StepperProps {
  side: 'home' | 'away';
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}

/**
 * Счёт вводится кнопками, а не с клавиатуры.
 *
 * Числовая клавиатура в Telegram перекрывает половину экрана и закрывает
 * вторую половину счёта — вводить «2 : 1» вслепую игрок не должен.
 */
function ScoreStepper({ side, value, disabled, onChange }: StepperProps) {
  return (
    <StaticSquircle
      width={175}
      height={67}
      cornerRadius={RADIUS.md}
      className={styles.stepInput}
    >
      <button
        type="button"
        className={styles.step}
        aria-label={`${side === 'home' ? 'Хозяева' : 'Гости'}: меньше`}
        data-testid={`score-${side}-minus`}
        disabled={disabled}
        onClick={() => {
          onChange(value - 1);
        }}
      >
        <img
          src="/images/matches/stepper-minus.svg"
          alt=""
          aria-hidden
          className={styles.stepIconMinus}
        />
      </button>
      <span className={styles.stepValue} data-testid={`score-${side}`}>
        {value}
      </span>
      <button
        type="button"
        className={styles.step}
        aria-label={`${side === 'home' ? 'Хозяева' : 'Гости'}: больше`}
        data-testid={`score-${side}-plus`}
        disabled={disabled}
        onClick={() => {
          onChange(value + 1);
        }}
      >
        <img
          src="/images/matches/stepper-plus.svg"
          alt=""
          aria-hidden
          className={styles.stepIconPlus}
        />
      </button>
    </StaticSquircle>
  );
}

interface Draft {
  outcome: MatchOutcome | null;
  firstGoal: FirstGoalOutcome | null;
  homeScore: number;
  awayScore: number;
  bothTeamsScore: boolean | null;
  totalOver: boolean | null;
}

/**
 * Собирает тело запроса под выбранный тип.
 *
 * `null` означает «выбор не сделан» и гасит кнопку. Счёт готов всегда:
 * 0:0 — такой же валидный Прогноз, как любой другой.
 */
function buildPayload(
  matchId: string,
  type: PredictionType,
  draft: Draft,
): CreatePredictionPayload | null {
  switch (type) {
    case PredictionType.MATCH_OUTCOME:
      return draft.outcome === null
        ? null
        : { matchId, type, outcome: draft.outcome };

    case PredictionType.FIRST_GOAL:
      return draft.firstGoal === null
        ? null
        : { matchId, type, firstGoal: draft.firstGoal };

    case PredictionType.EXACT_SCORE:
      return {
        matchId,
        type,
        homeScoreGuess: draft.homeScore,
        awayScoreGuess: draft.awayScore,
      };

    case PredictionType.BOTH_TEAMS_TO_SCORE:
      return draft.bothTeamsScore === null
        ? null
        : { matchId, type, bothTeamsScoreGuess: draft.bothTeamsScore };

    case PredictionType.TOTAL_OVER_UNDER:
      return draft.totalOver === null
        ? null
        : { matchId, type, totalOverGuess: draft.totalOver };
  }
}

/**
 * «Распределение прогнозов» — узел 438:50968.
 *
 * Не полоска с легендой, а строка из трёх сегментов: подпись и процент
 * лежат внутри своей доли. Подписи — П1 / Х / П2, как в ките, а не
 * названия команд: в сегмент шириной 10% название не помещается, ради
 * этого макет и сокращает.
 *
 * Ширина доли пропорциональна проценту (`flex-grow`), но не меньше своей
 * подписи (`min-width: min-content`). В ките у 10% нарисовано 72px — при
 * честной пропорции это 36px, и «П1 10%» туда не влезает. Схлопывать
 * подпись нельзя: сегмент без числа бесполезен.
 *
 * Зелёным подсвечивается **лидер** — исход, который выбрало больше всех.
 * В ките нарисовано состояние, где им оказался третий сегмент, но правило
 * не позиционное (подтверждено заказчиком).
 *
 * Нейтральные доли чередуются белым и серым, а не красятся одинаково:
 * иначе две соседние сливались бы в одну полосу, как только лидером
 * становится крайний сегмент.
 */
function OutcomeDistributionBar({
  distribution,
}: {
  distribution: OutcomeDistribution;
}) {
  const percents = toPercents(distribution);
  const leader = leaderOf(distribution);
  const parts: { outcome: MatchOutcome; label: string; percent: number }[] = [
    { outcome: 'HOME', label: 'П1', percent: percents.home },
    { outcome: 'DRAW', label: 'Х', percent: percents.draw },
    { outcome: 'AWAY', label: 'П2', percent: percents.away },
  ];

  return (
    <div className={styles.distribution} data-testid="outcome-distribution">
      <p className={styles.distributionTitle}>Распределение прогнозов</p>

      <div className={styles.distributionBar}>
        {parts.map((part) => (
          <div
            key={part.outcome}
            className={styles.distributionPart}
            data-outcome={part.outcome}
            data-leader={part.outcome === leader}
            // Доля приходит из данных сервера — класса под каждый процент
            // быть не может
            style={{ flexGrow: part.percent }}
            data-testid={`distribution-${part.outcome}`}
          >
            {part.label} {part.percent}%
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * «Прогноз игроков» — узел 823:8657, тот же приём, что OutcomeDistributionBar
 * выше, но на два сегмента: те же роли (`.distribution*`) одинаково честно
 * чередуют белый/серый и подсвечивают лидера зелёным для двух долей, что
 * для трёх — новых стилей не понадобилось.
 *
 * Порог в подписях — серверный `totalLine`, как и в вопросе над Да/Нет:
 * две подписи с зашитым числом разъехались бы с настройкой так же молча.
 * Нет порога — остаются «Больше»/«Меньше» без числа: доли это не меняет.
 */
function TotalDistributionBar({
  distribution,
  totalLine,
}: {
  distribution: TotalDistribution;
  totalLine: number | undefined;
}) {
  const percents = toTotalPercents(distribution);
  const leader = totalLeaderOf(distribution);
  const line = totalLine === undefined ? '' : ` ${String(totalLine)}`;
  const parts = [
    { key: 'OVER' as const, label: `Больше${line}`, percent: percents.over },
    { key: 'UNDER' as const, label: `Меньше${line}`, percent: percents.under },
  ];

  return (
    <div className={styles.distribution} data-testid="total-distribution">
      <p className={styles.distributionTitle}>Прогноз игроков</p>

      <div className={styles.distributionBar}>
        {parts.map((part) => (
          <div
            key={part.key}
            className={styles.distributionPart}
            data-outcome={part.key}
            data-leader={part.key === leader}
            style={{ flexGrow: part.percent }}
            data-testid={`total-distribution-${part.key}`}
          >
            {part.label} {part.percent}%
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Бинарный вопрос Да/Нет — BOTH_TEAMS_TO_SCORE и TOTAL_OVER_UNDER (узлы
 * 820:7784 / 823:8657). Тот же стиль кнопок, что у Исхода и Первого гола
 * (`.outcome`/`.outcomes`), плюс вопрос-подпись сверху — в остальных
 * типах такой строки нет.
 */
function BinaryQuestion({
  question,
  value,
  disabled,
  onChange,
}: {
  question: string | undefined;
  value: boolean | null;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {

  return (
    <div className={styles.binaryBlock}>
      {question ? <p className={styles.question}>{question}</p> : null}
      <div
        className={styles.outcomes}
        role="radiogroup"
        aria-label={question ?? 'Ответ'}
      >
        <Squircle asChild cornerRadius={RADIUS.md}>
          <button
            type="button"
            role="radio"
            aria-checked={value === true}
            className={styles.outcome}
            data-selected={value === true}
            data-testid="binary-yes"
            disabled={disabled}
            onClick={() => {
              onChange(true);
            }}
          >
            Да
          </button>
        </Squircle>
        <Squircle asChild cornerRadius={RADIUS.md}>
          <button
            type="button"
            role="radio"
            aria-checked={value === false}
            className={styles.outcome}
            data-selected={value === false}
            data-testid="binary-no"
            disabled={disabled}
            onClick={() => {
              onChange(false);
            }}
          >
            Нет
          </button>
        </Squircle>
      </div>
    </div>
  );
}
